import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNotification } from './NotificationContext';
import styles from './EmailPanel.module.css';

const apiUrl = 'https://api-backend.full-trucking-service.com/api';

// Labeluri Gmail cu icoane
const SYSTEM_LABELS = [
  { id: 'INBOX',          name: 'Inbox',    icon: '📥' },
  { id: 'SENT',           name: 'Sent',     icon: '📤' },
  { id: 'DRAFT',          name: 'Drafts',   icon: '📝' },
  { id: 'STARRED',        name: 'Starred',  icon: '⭐' },
  { id: 'IMPORTANT',      name: 'Important',icon: '🔖' },
  { id: 'SPAM',           name: 'Spam',     icon: '🚫' },
  { id: 'TRASH',          name: 'Trash',    icon: '🗑️' },
];

const EmailPanel = () => {
  const { notify } = useNotification();
  const token = sessionStorage.getItem('token');
  const config = {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    withCredentials: true,
  };

  const [isOpen, setIsOpen]                 = useState(false);
  const [isDragging, setIsDragging]         = useState(false);
  const [side, setSide]                     = useState(() => localStorage.getItem('emailPanelSide') || 'right');
  const [activeTab, setActiveTab]           = useState('inbox');
  const [emailStatus, setEmailStatus]       = useState({ connected: false });
  const [emails, setEmails]                 = useState([]);
  const [loadingEmails, setLoadingEmails]   = useState(false);
  const [selectedEmail, setSelectedEmail]   = useState(null);
  const [search, setSearch]                 = useState('');
  const [composeTo, setComposeTo]           = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody]       = useState('');
  const [sending, setSending]               = useState(false);
  const [previewAtt, setPreviewAtt]         = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Foldere / Labels
  const [labels, setLabels]                 = useState([]);
  const [activeLabel, setActiveLabel]       = useState('INBOX');
  const [showFolders, setShowFolders]       = useState(false);
  const [newLabelName, setNewLabelName]     = useState('');
  const [creatingLabel, setCreatingLabel]   = useState(false);
  const [showLabelMenu, setShowLabelMenu]   = useState(null); // messageId
  const [movingEmail, setMovingEmail]       = useState(false);
const [theme, setTheme] = useState(() => sessionStorage.getItem("theme") || "dark");

  const searchTimerRef = useRef(null);

  useEffect(() => { fetchEmailStatus(); }, []);

  useEffect(() => {
    if (isOpen && emailStatus.connected) {
      fetchEmails();
      fetchLabels();
    }
  }, [isOpen, emailStatus.connected]);

  useEffect(() => {
    if (isOpen && emailStatus.connected) fetchEmails();
  }, [activeLabel]);

  // Închide label menu la click în afară
  useEffect(() => {
    const close = () => setShowLabelMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
  const fetchTheme = async () => {
    try {
      const res = await axios.get(`${apiUrl}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTheme(res.data.theme || "dark");
    } catch {
      setTheme("dark");
    }
  };
  fetchTheme();
}, []);

  const toggleSide = () => {
    const newSide = side === 'right' ? 'left' : 'right';
    setSide(newSide);
    localStorage.setItem('emailPanelSide', newSide);
  };

  const fetchEmailStatus = async () => {
    try {
      const res = await axios.get(`${apiUrl}/email/status`, config);
      setEmailStatus(res.data);
    } catch {}
  };

  const fetchLabels = async () => {
    try {
      const res = await axios.get(`${apiUrl}/email/labels`, config);
      // Filtrăm doar labelurile custom (nu cele de sistem pe care le avem hardcodate)
      const systemIds = SYSTEM_LABELS.map(l => l.id);
      const custom = (res.data || []).filter(l =>
        !systemIds.includes(l.id) &&
        !l.id.startsWith('CATEGORY_') &&
        l.type !== 'system'
      );
      setLabels(custom);
    } catch {}
  };

  const fetchEmails = async (searchQuery = '') => {
    setLoadingEmails(true);
    try {
      const res = await axios.get(`${apiUrl}/email/inbox`, {
        ...config,
        params: { search: searchQuery, maxResults: 30, labelId: activeLabel },
      });
      setEmails(res.data);
    } catch {
      notify('Failed to load emails', 'error');
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchEmails(val), 500);
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;
    setCreatingLabel(true);
    try {
      const res = await axios.post(`${apiUrl}/email/labels`, { name: newLabelName.trim() }, config);
      setLabels(prev => [...prev, res.data]);
      setNewLabelName('');
      notify(`Label "${res.data.name}" created!`, 'success');
    } catch {
      notify('Failed to create label', 'error');
    } finally {
      setCreatingLabel(false);
    }
  };

  const handleMoveEmail = async (messageId, targetLabelId) => {
    setMovingEmail(true);
    setShowLabelMenu(null);
    try {
      // Adaugă noul label și elimină cel curent
      await axios.post(`${apiUrl}/email/move`, {
        messageId,
        addLabelIds: [targetLabelId],
        removeLabelIds: activeLabel !== 'ALL' ? [activeLabel] : [],
      }, config);
      // Scoate emailul din lista curentă
      setEmails(prev => prev.filter(e => e.id !== messageId));
      if (selectedEmail?.id === messageId) setSelectedEmail(null);
      const labelName = [...SYSTEM_LABELS, ...labels].find(l => l.id === targetLabelId)?.name || targetLabelId;
      notify(`Email moved to "${labelName}"`, 'success');
    } catch {
      notify('Failed to move email', 'error');
    } finally {
      setMovingEmail(false);
    }
  };

  const handleSend = async () => {
    if (!composeTo || !composeSubject || !composeBody) {
      notify('Please fill all fields', 'error');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${apiUrl}/email/send`, { to: composeTo, subject: composeSubject, body: composeBody }, config);
      notify('Email sent!', 'success');
      setComposeTo(''); setComposeSubject(''); setComposeBody('');
      setActiveTab('inbox');
    } catch {
      notify('Failed to send email', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleImportAttachment = async (att) => {
    try {
      await axios.post(`${apiUrl}/email/import-attachment`, {
        messageId: att.messageId,
        attachmentId: att.attachmentId,
        filename: att.filename,
        mimeType: att.mimeType,
        docType: 'other',
      }, config);
      notify(`"${att.filename}" imported to Documents!`, 'success');
    } catch {
      notify('Failed to import', 'error');
    }
  };

const handlePreviewAttachment = async (att) => {
  setLoadingPreview(true);
  setPreviewAtt(null);
  try {
    const res = await axios.post(
      `${apiUrl}/email/attachment-preview`,
      { messageId: att.messageId, attachmentId: att.attachmentId },
      { ...config, responseType: 'blob' }
    );
    const url = URL.createObjectURL(res.data);
    
    // Dacă e PDF, deschide direct în tab nou
    if (att.mimeType === 'application/pdf') {
      window.open(url, '_blank');
      setLoadingPreview(false);
      return;
    }
    
    setPreviewAtt({ url, mimeType: att.mimeType, filename: att.filename });
  } catch {
    notify('Preview failed', 'error');
  } finally {
    setLoadingPreview(false);
  }
};

  const handleAttachDragStart = (e, att) => {
    const attData = { ...att, messageId: selectedEmail.id };
    e.dataTransfer.setData('application/fts-attachment', JSON.stringify(attData));
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
  };

  const handleAttachDragEnd = () => setIsDragging(false);

  const isPreviewable = (mimeType) =>
    mimeType === 'application/pdf' || mimeType?.startsWith('image/');

  const allLabelsForMenu = [
    ...SYSTEM_LABELS.filter(l => l.id !== activeLabel),
    ...labels.filter(l => l.id !== activeLabel),
  ];

  const activeLabelInfo = SYSTEM_LABELS.find(l => l.id === activeLabel)
    || labels.find(l => l.id === activeLabel)
    || { name: activeLabel, icon: '📁' };

  return (
    <>
      {/* BUTON FLOATING */}
      <button
        className={`${styles.floatingBtn} ${isOpen ? styles.floatingBtnActive : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
        title="Email"
      >
        ✉
        {emailStatus.connected && <span className={styles.floatingDot} />}
      </button>

      {/* PANEL */}
     <div
  className={[
    styles.panel,
    side === 'left' ? styles.panelLeft : styles.panelRight,
    isOpen ? styles.panelOpen : '',
    theme === 'light' ? styles.lightMode : '',  // ← adaugă asta
  ].filter(Boolean).join(' ')}
  style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
>
        {/* HEADER */}
        <div className={styles.panelHeader}>
          <button className={styles.arrowBtn} onClick={toggleSide} title={side === 'right' ? 'Mută la stânga' : 'Mută la dreapta'}>
            {side === 'right' ? '◀' : '▶'}
          </button>
          <span className={styles.panelTitle}>
            ✉ {emailStatus.connected ? emailStatus.email : 'Email'}
          </span>
          <div className={styles.panelHeaderActions}>
            {emailStatus.connected && (
              <>
                <button
                  className={`${styles.tabBtn} ${activeTab === 'inbox' ? styles.tabBtnActive : ''}`}
                  onClick={() => { setActiveTab('inbox'); setSelectedEmail(null); }}
                >
                  Inbox
                </button>
                <button
                  className={`${styles.tabBtn} ${activeTab === 'compose' ? styles.tabBtnActive : ''}`}
                  onClick={() => setActiveTab('compose')}
                >
                  + New
                </button>
                <button
                  className={`${styles.tabBtn} ${showFolders ? styles.tabBtnActive : ''}`}
                  onClick={() => setShowFolders(prev => !prev)}
                  title="Folders"
                >
                  📁
                </button>
              </>
            )}
            <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>✕</button>
          </div>
        </div>

        {/* NOT CONNECTED */}
        {!emailStatus.connected && (
          <div className={styles.notConnected}>
            <p>No email connected.</p>
            <p>Go to <strong>Settings → Email Integration</strong> to connect Gmail.</p>
          </div>
        )}

        {emailStatus.connected && (
          <div className={styles.mainLayout}>

            {/* SIDEBAR FOLDERE */}
            {showFolders && (
              <div className={styles.folderSidebar}>
                <div className={styles.folderSidebarTitle}>Folders</div>

                {/* Foldere sistem */}
                {SYSTEM_LABELS.map(label => (
                  <button
                    key={label.id}
                    className={`${styles.folderItem} ${activeLabel === label.id ? styles.folderItemActive : ''}`}
                    onClick={() => {
                      setActiveLabel(label.id);
                      setActiveTab('inbox');
                      setSelectedEmail(null);
                      setSearch('');
                    }}
                  >
                    <span className={styles.folderIcon}>{label.icon}</span>
                    <span className={styles.folderName}>{label.name}</span>
                  </button>
                ))}

                {/* Separator + labeluri custom */}
                {labels.length > 0 && (
                  <>
                    <div className={styles.folderDivider}>Labels</div>
                    {labels.map(label => (
                      <button
                        key={label.id}
                        className={`${styles.folderItem} ${activeLabel === label.id ? styles.folderItemActive : ''}`}
                        onClick={() => {
                          setActiveLabel(label.id);
                          setActiveTab('inbox');
                          setSelectedEmail(null);
                          setSearch('');
                        }}
                      >
                        <span className={styles.folderIcon}>🏷️</span>
                        <span className={styles.folderName}>{label.name}</span>
                      </button>
                    ))}
                  </>
                )}

                {/* Creare label nou */}
                <div className={styles.newLabelRow}>
                  <input
                    type="text"
                    value={newLabelName}
                    onChange={e => setNewLabelName(e.target.value)}
                    placeholder="New label..."
                    className={styles.newLabelInput}
                    onKeyDown={e => e.key === 'Enter' && handleCreateLabel()}
                  />
                  <button
                    className={styles.newLabelBtn}
                    onClick={handleCreateLabel}
                    disabled={creatingLabel || !newLabelName.trim()}
                    title="Create label"
                  >
                    {creatingLabel ? '...' : '+'}
                  </button>
                </div>
              </div>
            )}

            {/* INBOX */}
            {activeTab === 'inbox' && (
              <div className={styles.inboxContainer}>
                {/* Breadcrumb folder activ */}
                <div className={styles.activeFolderBar}>
                  <span>{activeLabelInfo.icon} {activeLabelInfo.name}</span>
                  <div className={styles.searchRow}>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={handleSearch}
                      className={styles.searchInput}
                    />
                    <button className={styles.refreshBtn} onClick={() => fetchEmails(search)}>🔄</button>
                  </div>
                </div>

                <div className={styles.splitView}>
                  {/* Lista emailuri */}
                  <div className={styles.emailList}>
                    {loadingEmails ? (
                      <p className={styles.emptyMsg}>Loading...</p>
                    ) : emails.length === 0 ? (
                      <p className={styles.emptyMsg}>No emails found.</p>
                    ) : (
                      emails.map(msg => (
                        <div
                          key={msg.id}
                          className={[
                            styles.emailItem,
                            selectedEmail?.id === msg.id ? styles.emailItemActive : '',
                            !msg.isRead ? styles.emailItemUnread : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => { setSelectedEmail(msg); setPreviewAtt(null); }}
                        >
                          <div className={styles.emailItemHeader}>
                            <div className={styles.emailItemFrom}>{msg.from.replace(/<.*?>/g, '').trim()}</div>
                            {/* Buton mutare folder */}
                            <div
                              className={styles.moveBtn}
                              onClick={e => { e.stopPropagation(); setShowLabelMenu(showLabelMenu === msg.id ? null : msg.id); }}
                              title="Move to folder"
                            >
                              📁
                              {showLabelMenu === msg.id && (
                                <div className={styles.labelMenu} onClick={e => e.stopPropagation()}>
                                  <div className={styles.labelMenuTitle}>Move to</div>
                                  {allLabelsForMenu.map(label => (
                                    <button
                                      key={label.id}
                                      className={styles.labelMenuItem}
                                      onClick={() => handleMoveEmail(msg.id, label.id)}
                                      disabled={movingEmail}
                                    >
                                      {label.icon || '🏷️'} {label.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={styles.emailItemSubject}>
                            {!msg.isRead && <span className={styles.unreadDot} />}
                            {msg.subject}
                          </div>
                          <div className={styles.emailItemSnippet}>{msg.snippet}</div>
                          <div className={styles.emailItemMeta}>
                            <span>{new Date(msg.date).toLocaleDateString()}</span>
                            {msg.attachments?.length > 0 && (
                              <span className={styles.attachBadge}>📎 {msg.attachments.length}</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Detalii email */}
                  <div className={styles.emailDetail}>
                    {!selectedEmail ? (
                      <div className={styles.detailEmpty}><p>Select an email</p></div>
                    ) : (
                      <>
                        <div className={styles.detailHeader}>
                          <h4 className={styles.detailSubject}>{selectedEmail.subject}</h4>
                          <p className={styles.detailFrom}><strong>From:</strong> {selectedEmail.from}</p>
                          <p className={styles.detailDate}>{new Date(selectedEmail.date).toLocaleString()}</p>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                            <button
                              className={styles.replyBtn}
                              onClick={() => {
                                setComposeTo(selectedEmail.from.match(/<(.+?)>/)?.[1] || selectedEmail.from);
                                setComposeSubject(`Re: ${selectedEmail.subject}`);
                                setComposeBody('');
                                setActiveTab('compose');
                              }}
                            >
                              ↩ Reply
                            </button>
                            {/* Mută emailul din detalii */}
                            <div style={{ position: 'relative' }}>
                              <button
                                className={styles.replyBtn}
                                style={{ borderColor: 'rgba(96,165,250,0.4)', color: '#60A5FA', background: 'rgba(96,165,250,0.08)' }}
                                onClick={e => { e.stopPropagation(); setShowLabelMenu(showLabelMenu === `detail_${selectedEmail.id}` ? null : `detail_${selectedEmail.id}`); }}
                              >
                                📁 Move
                              </button>
                              {showLabelMenu === `detail_${selectedEmail.id}` && (
                                <div className={styles.labelMenu} onClick={e => e.stopPropagation()}>
                                  <div className={styles.labelMenuTitle}>Move to</div>
                                  {allLabelsForMenu.map(label => (
                                    <button
                                      key={label.id}
                                      className={styles.labelMenuItem}
                                      onClick={() => handleMoveEmail(selectedEmail.id, label.id)}
                                      disabled={movingEmail}
                                    >
                                      {label.icon || '🏷️'} {label.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {previewAtt ? (
                          <div className={styles.attPreviewContainer}>
                            <div className={styles.attPreviewBar}>
                              <span className={styles.attPreviewName}>{previewAtt.filename}</span>
                              <button className={styles.attPreviewClose} onClick={() => setPreviewAtt(null)}>✕ Close</button>
                            </div>
                  {previewAtt.mimeType === 'application/pdf' ? (
  <object
    data={previewAtt.url}
    type="application/pdf"
    className={styles.attPreviewFrame}
    style={{ width: '100%', height: '100%' }}
  >
    <a href={previewAtt.url} download={previewAtt.filename} className={styles.replyBtn}>
      ⬇ Download PDF
    </a>
  </object>
) : (
  <iframe src={previewAtt.url} className={styles.attPreviewFrame} title="preview" />
)}
                          </div>
                        ) : (
                          <div className={styles.detailBody}>
                            {selectedEmail.body ? (
                             <iframe srcDoc={selectedEmail.body} className={styles.detailIframe} title="email" sandbox="allow-same-origin allow-scripts allow-popups" />
                            ) : (
                              <p className={styles.detailSnippet}>{selectedEmail.snippet}</p>
                            )}
                          </div>
                        )}

                        {selectedEmail.attachments?.length > 0 && (
                          <div className={styles.attachments}>
                            <p className={styles.attachTitle}>
                              Attachments ({selectedEmail.attachments.length})
                            </p>
                            {selectedEmail.attachments.map(att => (
                              <div
                                key={att.attachmentId}
                                className={styles.attachItem}
                                draggable={true}
                                onDragStart={e => handleAttachDragStart(e, att)}
                                onDragEnd={handleAttachDragEnd}
                                style={{ userSelect: 'none', WebkitUserDrag: 'element' }}
                              >
                                <div className={styles.attachItemLeft}>
                                  <span className={styles.dragHandle}>⠿</span>
                                  <span className={styles.attachName}>
                                    {att.mimeType === 'application/pdf' ? '📄' : att.mimeType?.startsWith('image/') ? '🖼' : '📎'}{' '}
                                    {att.filename}
                                    {att.size ? <span className={styles.attachSize}> ({(att.size / 1024).toFixed(0)} KB)</span> : null}
                                  </span>
                                </div>
                                <div className={styles.attachActions}>
                                  {loadingPreview ? (
                                    <span className={styles.attachLoading}>...</span>
                                  ) : (
                                    <button
  className={styles.previewBtn}
  onClick={() => {
    const url = `${apiUrl}/email/attachment-view/${att.messageId || selectedEmail.id}/${att.attachmentId}`;
    window.open(url, '_blank');
  }}
  title="Preview"
>
  👁
</button>
                                  )}
                                  <button className={styles.importBtn} onClick={() => handleImportAttachment(att)} title="Import">Import</button>
                                  {document.querySelector('[class*="ultimateaddloadsection"]') && (
                                    <button
                                      className={styles.importBtn}
                                      style={{ background: 'rgba(96,165,250,0.15)', borderColor: 'rgba(96,165,250,0.4)', color: '#60A5FA' }}
                                      onClick={async () => {
                                        try {
                                          const res = await axios.post(
                                            `${apiUrl}/email/attachment-preview`,
                                            { messageId: att.messageId || selectedEmail.id, attachmentId: att.attachmentId },
                                            { ...config, responseType: 'blob' }
                                          );
                                          const file = new File([res.data], att.filename, { type: att.mimeType });
                                          window.dispatchEvent(new CustomEvent('email-attachment-as-file', { detail: { file } }));
                                          notify(`"${att.filename}" `, 'success');
                                          setIsOpen(false);
                                        } catch { notify('Failed', 'error'); }
                                      }}
                                      title="Use as RC"
                                    >📄 RC</button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* COMPOSE */}
            {activeTab === 'compose' && (
              <div className={styles.composeContainer}>
                <div className={styles.composeField}>
                  <label>To</label>
                  <input type="email" value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="recipient@example.com" className={styles.composeInput} />
                </div>
                <div className={styles.composeField}>
                  <label>Subject</label>
                  <input type="text" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Subject" className={styles.composeInput} />
                </div>
                <div className={styles.composeField}>
                  <label>Message</label>
                  <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder="Write your message..." className={styles.composeTextarea} rows={12} />
                </div>
                <button className={styles.sendBtn} onClick={handleSend} disabled={sending}>
                  {sending ? 'Sending...' : '📤 Send Email'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isOpen && !isDragging && (
        <div className={styles.overlay} onClick={() => setIsOpen(false)} />
      )}
    </>
  );
};

export default EmailPanel;